/**
 * iter845k — MyFatoorah payment callback landing page.
 *
 * Route: /order/:branchSlug/payment/callback?order_id=…&paymentId=…
 *
 * MyFatoorah redirects the customer here after their card / Mada /
 * Apple Pay / STC Pay transaction. We call our backend to verify
 * the payment server-side and then render a thank-you or retry state.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Clock, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function PublicPaymentCallbackPage() {
  const { branchSlug } = useParams();
  const [params] = useSearchParams();
  const paymentId = params.get('paymentId') || params.get('Id') || params.get('id');
  const orderId = params.get('order_id');
  const [state, setState] = useState({ loading: true, result: null, error: null });

  useEffect(() => {
    let cancel = false;
    const verify = async () => {
      if (!paymentId || !orderId) {
        setState({ loading: false, result: null, error: 'Missing paymentId or order_id in URL.' });
        return;
      }
      try {
        const { data } = await axios.post(`${API}/public/payments/verify`, {
          payment_id: paymentId,
          order_id: orderId,
        });
        if (cancel) return;
        setState({ loading: false, result: data, error: null });
      } catch (err) {
        if (cancel) return;
        setState({ loading: false, result: null, error: err.response?.data?.detail || 'Payment verification failed.' });
      }
    };
    verify();
    return () => { cancel = true; };
  }, [paymentId, orderId]);

  const paid = state.result?.ok && state.result?.status === 'paid';

  if (state.loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="payment-callback-loading">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-stone-200 flex items-center justify-center mx-auto mb-3 animate-pulse">
            <Clock size={22} className="text-stone-500" />
          </div>
          <div className="text-sm text-stone-600">Verifying your payment…</div>
        </div>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-stone-50 flex items-center justify-center px-4" data-testid="payment-callback-success">
        <div className="max-w-md w-full text-center bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} />
          </div>
          <h1 className="text-2xl font-bold text-stone-900">Payment received!</h1>
          <p className="mt-2 text-sm text-stone-600">Your order #{state.result.order_number} has been confirmed and sent to the kitchen.</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-left">
            <div className="bg-stone-50 rounded-2xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-stone-500">Order #</div>
              <div className="text-lg font-bold" data-testid="callback-order-number">{state.result.order_number}</div>
            </div>
            <div className="bg-stone-50 rounded-2xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-stone-500">Total</div>
              <div className="text-lg font-bold">{state.result.currency_code} {Number(state.result.total || 0).toFixed(2)}</div>
            </div>
          </div>
          <Link to={`/order/${branchSlug}`} className="mt-6 inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900">
            <ArrowLeft size={14} /> Back to menu
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="payment-callback-failed">
      <div className="max-w-md w-full text-center bg-white rounded-3xl shadow-xl p-6 sm:p-8">
        <div className="w-16 h-16 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4">
          <XCircle size={32} />
        </div>
        <h1 className="text-2xl font-bold text-stone-900">Payment not completed</h1>
        <p className="mt-2 text-sm text-stone-600">{state.error || state.result?.reason || 'The transaction was cancelled or failed. You can try again.'}</p>
        <Link to={`/order/${branchSlug}`} className="mt-6 inline-flex items-center gap-1.5 text-sm px-4 h-10 rounded-full bg-stone-900 text-white font-semibold">
          <ArrowLeft size={14} /> Back to menu
        </Link>
      </div>
    </div>
  );
}
